import React, { useState } from 'react';
import { Store, Plus, Link2 } from 'lucide-react';
import { motion } from 'framer-motion';

const API_URL = process.env.REACT_APP_STORE_API_URL || 'https://v3ugnvc3qk.execute-api.us-west-2.amazonaws.com/prod';

/**
 * Helper function to append user_id query parameter to admin URLs
 */
const appendUserId = (url, userId) => {
    if (!userId) return url;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}user_id=${encodeURIComponent(userId)}`;
};

/**
 * Modal to let user choose between linking to existing store or creating new one
 */
export const LinkOrCreateStoreModal = ({
    onClose,
    onCreateNew,
    onLinkExisting,
    userStores = [],
    businessId,
    userId
}) => {
    const [selectedStoreId, setSelectedStoreId] = useState(null);
    const [linking, setLinking] = useState(false);
    const [error, setError] = useState(null);

    const handleLinkStore = async () => {
        if (!selectedStoreId) {
            setError('אנא בחר חנות');
            return;
        }

        setLinking(true);
        setError(null);

        try {
            // Link the business to the existing store
            const url = appendUserId(`${API_URL}/admin/store/${selectedStoreId}/link-business`, userId);
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    business_id: businessId
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'שגיאה בקישור החנות');
            }

            const result = await response.json();

            // Call the callback with the linked store data
            if (onLinkExisting) {
                onLinkExisting(result);
            }

            onClose();
        } catch (err) {
            console.error('Error linking store:', err);
            setError(err.message);
        } finally {
            setLinking(false);
        }
    };

    return (
        <div
            className="fixed inset-0 bg-black/50 backdrop-blur-md flex items-center justify-center z-[10001] p-4"
            onClick={onClose}
        >
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-2xl shadow-elevated max-w-2xl w-full mx-4 max-h-[90vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
                dir="rtl"
            >
                {/* Header */}
                <div className="px-8 py-7 border-b border-line-subtle">
                    <h2 className="text-3xl font-bold text-ink-strong mb-1">בחר אפשרות</h2>
                    <p className="text-ink-subtle text-sm">האם ברצונך לקשר את העסק לחנות קיימת או ליצור חנות חדשה?</p>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto px-8 py-8 space-y-6">
                    {error && (
                        <div className="bg-danger-50 border border-danger-100 text-danger-700 px-5 py-4 rounded-xl text-sm font-medium flex items-start gap-3">
                            <div className="text-lg mt-0.5">⚠️</div>
                            <div>{error}</div>
                        </div>
                    )}

                    {/* Create New Store Option */}
                    <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                            onCreateNew();
                            onClose();
                        }}
                        className="w-full p-6 bg-gradient-to-br from-success-50 to-success-50 border-2 border-success-100 rounded-xl hover:border-success-100 hover:shadow-lg transition-all duration-200 group"
                    >
                        <div className="flex items-start gap-4">
                            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-success-500 to-success-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                                <Plus className="w-7 h-7 text-white" strokeWidth={2.5} />
                            </div>
                            <div className="flex-1 text-right">
                                <h3 className="text-xl font-bold text-ink-strong mb-2">צור חנות חדשה</h3>
                                <p className="text-sm text-ink-muted">
                                    פתח חנות חדשה עבור העסק הזה עם שם ייחודי והגדרות נפרדות
                                </p>
                            </div>
                        </div>
                    </motion.button>

                    {/* Divider */}
                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-line"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-4 bg-white text-ink-subtle font-medium">או</span>
                        </div>
                    </div>

                    {/* Link to Existing Store Option */}
                    <div className="space-y-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-info-500 to-brand-500 flex items-center justify-center">
                                <Link2 className="w-5 h-5 text-white" strokeWidth={2.5} />
                            </div>
                            <h3 className="text-xl font-bold text-ink-strong">קשר לחנות קיימת</h3>
                        </div>
                        <p className="text-sm text-ink-muted pr-13">בחר אחת מהחנויות שלך כדי לקשר אותה לעסק זה</p>

                        {/* Store List */}
                        <div className="space-y-3 max-h-64 overflow-y-auto">
                            {userStores.length === 0 ? (
                                <div className="text-center py-8 bg-surface-muted rounded-xl border border-line">
                                    <Store className="w-12 h-12 text-ink-subtle mx-auto mb-2" />
                                    <p className="text-sm text-ink-muted">אין לך חנויות קיימות</p>
                                </div>
                            ) : (
                                userStores.map((store) => (
                                    <motion.button
                                        key={store.store_id}
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                        onClick={() => setSelectedStoreId(store.store_id)}
                                        className={`w-full p-4 rounded-xl border-2 transition-all duration-200 text-right ${
                                            selectedStoreId === store.store_id
                                                ? 'border-info-500 bg-info-50 shadow-md'
                                                : 'border-line bg-white hover:border-info-100 hover:bg-blue-25'
                                        }`}
                                    >
                                        <div className="flex items-center gap-4">
                                            {store.profile_image_url ? (
                                                <img
                                                    src={store.profile_image_url}
                                                    alt={store.name}
                                                    className="w-12 h-12 rounded-full object-cover border-2 border-line"
                                                />
                                            ) : (
                                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-surface-sunken to-line flex items-center justify-center">
                                                    <Store className="w-6 h-6 text-ink-subtle" />
                                                </div>
                                            )}
                                            <div className="flex-1">
                                                <h4 className="font-bold text-ink-strong">{store.name}</h4>
                                                {store.about && (
                                                    <p className="text-xs text-ink-muted line-clamp-1">{store.about}</p>
                                                )}
                                            </div>
                                            {selectedStoreId === store.store_id && (
                                                <div className="w-6 h-6 rounded-full bg-info-500 flex items-center justify-center flex-shrink-0">
                                                    <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                </div>
                                            )}
                                        </div>
                                    </motion.button>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="px-8 py-6 border-t border-line-subtle bg-surface-muted rounded-b-2xl flex justify-end gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-7 py-3 rounded-lg bg-white text-ink font-semibold transition-all duration-200 hover:bg-surface-muted border border-line active:scale-95"
                        disabled={linking}
                    >
                        ביטול
                    </button>
                    {userStores.length > 0 && (
                        <button
                            type="button"
                            onClick={handleLinkStore}
                            className="px-7 py-3 rounded-lg bg-gradient-to-r from-info-500 to-brand-500 text-white font-semibold transition-all duration-200 hover:from-info-600 hover:to-brand-600 disabled:from-line disabled:to-line-strong disabled:cursor-not-allowed shadow-sm hover:shadow-md active:scale-95"
                            disabled={!selectedStoreId || linking}
                        >
                            {linking ? (
                                <span className="flex items-center gap-2.5">
                                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                    מקשר...
                                </span>
                            ) : (
                                'קשר חנות'
                            )}
                        </button>
                    )}
                </div>
            </motion.div>
        </div>
    );
};
